import { Box } from "@mui/material"
import type { FC, ReactNode } from "react"
import { Fragment, useMemo } from "react"
import { useFormContext } from "react-hook-form"

import { FormAutocomplete, FormSelect } from "@/components/UiKit/FormControls"
import { FREIGHT_TERMS_TO_LABEL, INCOTERMS_TO_LABEL } from "@/constants/shippingTerms"
import { FreightTerms, Incoterms } from "@/graphql/codegen/graphql"
import { useGetPurchaseOrderIncotermsLocations } from "@/screens/Procurement/PurchaseOrder/hooks/useGetPurchaseOrderIncotermsLocations"
import type { Option } from "@/types/form-fields"

const FREIGHT_OPTIONS: Option<FreightTerms>[] = Object.values(FreightTerms).map((value) => ({
  value,
  label: FREIGHT_TERMS_TO_LABEL[value],
}))

const INCOTERMS_OPTIONS: Option<Incoterms>[] = Object.values(Incoterms).map((value) => ({
  value,
  label: INCOTERMS_TO_LABEL[value],
}))

type ShippingTermsField = "freightTerms" | "incoterms" | "incotermsLocationId"

const DEFAULT_FIELD_ORDER: ShippingTermsField[] = ["freightTerms", "incoterms", "incotermsLocationId"]

interface ShippingTermsFieldGroupProps {
  testIdPrefix: string
  fieldOrder?: ShippingTermsField[]
}

export const ShippingTermsFieldGroup: FC<ShippingTermsFieldGroupProps> = ({
  testIdPrefix,
  fieldOrder = DEFAULT_FIELD_ORDER,
}) => {
  const { control } = useFormContext()
  const { locations, isFetchingLocations, loadMoreLocations } = useGetPurchaseOrderIncotermsLocations()

  const incotermsLocationOptions = useMemo<Option<string>[]>(
    () =>
      locations.map((location) => ({
        value: location.locationId,
        label: [location.name, location.city, location.region?.name].filter(Boolean).join(", "),
      })),
    [locations]
  )

  const fieldByName: Record<ShippingTermsField, ReactNode> = {
    freightTerms: (
      <FormSelect
        control={control}
        name='freightTerms'
        label='Freight Terms'
        required
        placeholder='Select freight terms'
        options={FREIGHT_OPTIONS}
        data-testid={`${testIdPrefix}.freight-field`}
      />
    ),
    incoterms: (
      <FormSelect
        control={control}
        name='incoterms'
        label='Incoterms'
        required
        placeholder='Select incoterms'
        options={INCOTERMS_OPTIONS}
        data-testid={`${testIdPrefix}.incoterms-field`}
      />
    ),
    incotermsLocationId: (
      <FormAutocomplete
        control={control}
        name='incotermsLocationId'
        label='Incoterms Location'
        required
        placeholder='Select location'
        options={incotermsLocationOptions}
        loading={isFetchingLocations}
        onInputChange={() => loadMoreLocations()}
        data-testid={`${testIdPrefix}.incoterms-location-field`}
      />
    ),
  }

  return (
    <Box className='flex flex-col gap-y-4'>
      {fieldOrder.map((name) => (
        <Fragment key={name}>{fieldByName[name]}</Fragment>
      ))}
    </Box>
  )
}
